import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";
import {
  getUserProfileUserProfileGetQueryKey,
  useUpdateUserProfileEndpointUserProfilePatch,
} from "../kubb/hooks/UserHooks";
import { ProfileContext, useProfile } from "../reducers/profile";

// Feature IDs must follow the format: YYYY-MM-DD-feature-name
// e.g., 2026-01-15-export-units
const FEATURE_ID_PATTERN = /^\d{4}-\d{2}-\d{2}-[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Validates that a feature ID follows the required format.
 * @param featureId - The feature ID to validate
 * @returns true if valid, throws Error if invalid
 */
function validateFeatureId(featureId: string): void {
  if (!FEATURE_ID_PATTERN.test(featureId)) {
    throw new Error(
      `Invalid feature ID: "${featureId}". Feature IDs must follow format: YYYY-MM-DD-feature-name (e.g., 2026-01-15-export-units)`,
    );
  }
}

interface UseFeatureAcknowledgementResult {
  /** Whether the feature has been acknowledged by the user */
  isAcknowledged: boolean;
  /** Function to acknowledge the feature */
  acknowledge: () => void;
  /** Whether the acknowledgement is being persisted */
  isLoading: boolean;
  /** Whether the user profile is still loading */
  isProfileLoading: boolean;
}

/**
 * Hook to manage feature acknowledgement state for dismissible banners.
 *
 * Uses the user profile to check if a feature has been acknowledged,
 * and provides a function to acknowledge (dismiss) the feature.
 *
 * @param featureId - Unique identifier for the feature in format YYYY-MM-DD-feature-name (e.g., "2026-01-15-export-units")
 * @returns Object with acknowledgement state and acknowledge function
 */
export function useFeatureAcknowledgement(
  featureId: string,
): UseFeatureAcknowledgementResult {
  // Validate feature ID format on hook initialization
  validateFeatureId(featureId);
  const profile = useProfile();
  const { dispatch } = useContext(ProfileContext);
  const queryClient = useQueryClient();

  // Local state for optimistic update (used only until context is updated)
  const [optimisticallyDismissed, setOptimisticallyDismissed] = useState(false);

  const { mutateAsync, isPending } =
    useUpdateUserProfileEndpointUserProfilePatch();

  // Check if the feature has been acknowledged
  const isAcknowledged =
    optimisticallyDismissed ||
    Boolean(profile?.profile?.acknowledgements?.[featureId]);

  // Check if user profile is still loading
  const isProfileLoading = profile === undefined;

  const acknowledge = useCallback(async () => {
    const acknowledgedAt = new Date().toISOString();

    // Optimistic update - hide banner immediately via local state
    setOptimisticallyDismissed(true);

    // Update ProfileContext immediately (optimistic) so navigation works
    // This ensures the acknowledgement persists even if user navigates away
    // before the API call completes
    dispatch({
      type: "PROFILE_ACKNOWLEDGEMENT_UPDATE",
      payload: {
        acknowledgements: {
          [featureId]: acknowledgedAt,
        },
      },
    });

    try {
      await mutateAsync({
        data: {
          acknowledgements: {
            [featureId]: acknowledgedAt,
          },
        },
      });

      // Invalidate (don't refetch) the profile cache to ensure fresh data on next fetch.
      // We use invalidateQueries instead of refetchQueries because:
      // 1. The ProfileContext optimistic update already handles immediate UI state
      // 2. If component unmounts before refetch completes, ProfileContext still has the data
      // 3. Next profile fetch will get fresh server data regardless
      queryClient.invalidateQueries({
        queryKey: getUserProfileUserProfileGetQueryKey(),
      });
    } catch (error) {
      // On error, keep the banner hidden (graceful degradation)
      // The user already clicked "Got it", so don't annoy them
      // Note: The optimistic ProfileContext update persists even on error,
      // which is intentional - user won't see the banner again this session
      console.error("Failed to persist feature acknowledgement:", error);
    }
  }, [featureId, mutateAsync, dispatch, queryClient]);

  return {
    isAcknowledged,
    acknowledge,
    isLoading: isPending,
    isProfileLoading,
  };
}
